import { useEffect, useState, SetStateAction, Dispatch } from 'react';
import { useTranslation } from 'next-i18next';
import { useGetListSellingOfferNegotiations } from '@hooks/negotiations/useOffersListQueries';
import { DEFAULT_SELLING_OFFERS_NEGOTIATIONS_PER_PAGE, SELLING_OFFERS_PATH } from '@constants/negotiation';
import NegotiationListingsErrorState from '@components/organisms/negotiation-listings/error-state/negotiation-listings-error-state';
import { useListItemSelectContext } from '@context/negotiation-listings-split-view.context';
import { NegotiationProductCard } from '@components/organisms/selling-offers/negotiation-product-card/negotiation-product-card';
import useWindowSize from '@hooks/use-window-size';
import Icon from '@components/atoms/icon/icon';
import SellingOffersNegotiationCardSkeleton from '@components/organisms/catalog/components/skeleton-components/selling-offers-skeleton/selling-offers-negotiation-card-skeleton';
import { SkeletonList } from '@components/organisms/catalog/components/skeleton-components/skeleton-list';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import SellingOffersNegotiationColumnTopSkeleton from '@components/organisms/catalog/components/skeleton-components/selling-offers-skeleton/selling-offers-negotiation-column-top-skeleton';
import { PaginationState } from '@interfaces/models/negotiation/domain/BuyingOffer';
import { SellingOffersNegotiationData } from '@interfaces/models/negotiation/domain/SellingOffersNegotiation';
import { useRouter } from 'next/router';
import SellingOffersNegotiationPagination from '../selling-offers-negotiation-pagination/selling-offers-negotiation-pagination';
import SellingOffersNegotiationsList from '../selling-offers-negotiations-list/selling-offers-negotiations-list';
import styles from '../selling-offers.module.scss';

interface SellingOffersNegotiationsColumnProps {
  sellingOffersNegotiationsPaginationMeta: PaginationState;
  setSellingOffersNegotiationsList: Dispatch<SetStateAction<SellingOffersNegotiationData[]>>;
  sellingOffersNegotiationsList: SellingOffersNegotiationData[];
}

const SellingOffersNegotiationsColumn: React.FC<SellingOffersNegotiationsColumnProps> = ({
  sellingOffersNegotiationsPaginationMeta,
  setSellingOffersNegotiationsList,
  sellingOffersNegotiationsList,
}) => {
  const [activePage, setActivePage] = useState(1);
  const resultsPerPage = DEFAULT_SELLING_OFFERS_NEGOTIATIONS_PER_PAGE;
  const limit = activePage * resultsPerPage > 100 ? 100 : activePage * resultsPerPage;
  const { selectedListItemId, setSelectedListItemId, selectedListItemProduct } = useListItemSelectContext();
  const { isLoading, isFetching, error, refetch, isRefetching, isRefetchError } = useGetListSellingOfferNegotiations(
    selectedListItemId,
    'seller',
    limit,
    0,
  );
  const totalCount = sellingOffersNegotiationsPaginationMeta?.totalCount || 0;
  const { isWiderThanLg } = useWindowSize();
  const { t } = useTranslation('negotiation');
  const { sendAnalyticEvent } = useAnalyticEvents('selling_offers');
  const router = useRouter();

  const resetList = () => {
    setActivePage(1);
    setSellingOffersNegotiationsList([]);
  };

  useEffect(() => {
    resetList();
  }, [selectedListItemId]);

  const onLoadMore = () => {
    setActivePage((prevPage) => prevPage + 1);
    sendAnalyticEvent('mmao_view_all_offers', {
      product_id: selectedListItemId,
    });
  };

  const goBackToOffers = () => {
    setSelectedListItemId(null);
    router.push(SELLING_OFFERS_PATH, undefined, { shallow: true });
  };

  if (error || isRefetchError) {
    return (
      <div className={styles.sellingOffers__negotiationsListColumn}>
        <NegotiationListingsErrorState />
      </div>
    );
  }

  return (
    <div className={styles.sellingOffers__negotiationsListColumn}>
      {!isWiderThanLg && (
        <div className={styles.sellingOffers__negotiationsListColumn__backBtnWrapper}>
          <a
            className={styles.sellingOffers__negotiationsListColumn__backBtn}
            onClick={() => goBackToOffers()}
            data-cy="selling_offers_back_btn"
          >
            <Icon
              name="ios-arrow-left-20"
              className={styles.sellingOffers__negotiationsListColumn__backBtn__icon}
              ariaHidden
            />
            <span
              className={styles.sellingOffers__negotiationsListColumn__backBtn__text}
              data-cy="selling_offers_back_btn_label"
            >
              {t('NEGOTIATION_LISTING.SELLING_OFFERS.BACK_TO_OFFERS')}
            </span>
          </a>
        </div>
      )}
      {selectedListItemProduct?.picturePath ? (
        <>
          <NegotiationProductCard product={selectedListItemProduct} />
          <div
            className={styles.sellingOffers__negotiationsListColumn__offersCount}
            data-cy="selling_offers_offer_count"
          >
            <p>
              {t('NEGOTIATION_LISTING.SELLING_OFFERS.OFFERS')} {!!totalCount && `(${totalCount})`}
            </p>
          </div>
        </>
      ) : (
        <SellingOffersNegotiationColumnTopSkeleton />
      )}
      {sellingOffersNegotiationsList && sellingOffersNegotiationsList.length > 0 && (
        <>
          <SellingOffersNegotiationsList
            negotiationsList={sellingOffersNegotiationsList}
            refetch={() => refetch()}
          />
          <SellingOffersNegotiationPagination
            loading={isLoading || isFetching || isRefetching}
            onLoadMore={onLoadMore}
            totalCount={totalCount}
            currentLength={sellingOffersNegotiationsList.length}
          />
        </>
      )}
      {(isLoading || isFetching || isRefetching) && (
        <SkeletonList SkeletonComponent={SellingOffersNegotiationCardSkeleton} />
      )}
    </div>
  );
};

export default SellingOffersNegotiationsColumn;
